"use client";

import Image from "next/image";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

type Testimonial = {
  quote: string;
  name: string;
  title: string;
  avatar: string;
};

const testimonials: Testimonial[] = [
  {
    quote:
      "After I installed LovedByAI, my site’s popularity grew by 77% and I didn’t even change anything.",
    name: "Inbar Rotshild",
    title: "Content Creator",
    avatar: "/testimonials/inbar_icon.jpg",
  },
  {
    quote:
      "LovedByAI increased our AI search traffic significantly and revealed which topics captured the most interest.",
    name: "Eli Lepkifker",
    title: "CTO at Tagado",
    avatar: "/testimonials/hero_testimonial.webp",
  },
  {
    quote:
      "LovedByAI helped my business become more discoverable through AI and search, and the results have been outstanding.",
    name: "Crista Cloutier",
    title: "Owner, The Working Artist",
    avatar: "/clients/crista.webp",
  },
  {
    quote:
      "I'm no 'LLM expert.' I just installed the plugin, forgot about it, and suddenly started seeing a huge traffic lift.",
    name: "Sarah Jackson",
    title: "Blog Writer",
    avatar: "/testimonials/sarah_jackson.webp",
  },
  {
    quote:
      "We started showing up in ChatGPT and Claude in 2-3 weeks.",
    name: "Upton Underwood",
    title: "Owner, Local HVAC Company",
    avatar: "/testimonials/Upton_underwood.webp",
  },
];

// Crista (index 2) top, Sarah (index 3) bottom
const leftTestimonials = [testimonials[2], testimonials[3]];
// Inbar (index 0) top, Ari (index 4) bottom
const rightTestimonials = [testimonials[0], testimonials[4]];
// Eli is the center testimonial (the person in the hero image)
const centerTestimonial = testimonials[1];
const heroImage = "/testimonials/hero_testimonial.webp";

function StarRating() {
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className="w-4 h-4 fill-orange-400 text-orange-400"
        />
      ))}
    </div>
  );
}

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <div className="relative bg-white rounded-2xl p-7 pb-12 border border-black/[0.06] shadow-[0_2px_10px_rgba(16,24,40,0.05)] flex flex-col gap-4 overflow-hidden">
      <StarRating />
      <p className="text-gray-700 text-sm leading-relaxed">
        "{testimonial.quote}"
      </p>
      <div className="flex items-center gap-3 mt-4">
        <Image
          src={testimonial.avatar}
          alt={testimonial.name}
          width={40}
          height={40}
          sizes="80px"
          className="w-10 h-10 rounded-full object-cover"
        />
        <div>
          <div className="font-semibold text-gray-900 text-sm">
            {testimonial.name}
          </div>
          <div className="text-gray-500 text-xs">{testimonial.title}</div>
        </div>
      </div>
      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none" />
    </div>
  );
}

function CenterFeatureCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <div className="flex flex-col items-center gap-4">
      {/* Hero Image */}
      <div className="relative">
        <Image
          src={heroImage}
          alt="Featured testimonial"
          width={320}
          height={400}
          className="w-full max-w-[320px] h-auto rounded-2xl object-cover shadow-lg"
        />
        {/* White fade at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white via-white/80 to-transparent rounded-b-2xl" />
      </div>
      
      {/* Quote Box - separate card below image */}
      <div className="relative bg-white rounded-2xl p-7 pb-12 border border-black/[0.06] shadow-[0_2px_10px_rgba(16,24,40,0.05)] w-full max-w-[320px] -mt-12 overflow-hidden">
        <div className="flex justify-center mb-3">
          <StarRating />
        </div>
        <p className="text-gray-700 text-sm leading-relaxed text-center">
          "{testimonial.quote}"
        </p>
        <div className="flex items-center justify-center gap-2 mt-4">
          <div className="text-center">
            <div className="font-semibold text-gray-900 text-sm">
              {testimonial.name}
            </div>
            <div className="text-gray-500 text-xs">{testimonial.title}</div>
          </div>
        </div>
        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none" />
      </div>
    </div>
  );
}

type TestimonialsCarouselSectionProps = {
  className?: string;
  headerText?: string;
};

export default function TestimonialsCarouselSection({
  className,
  headerText = "Early adopters report up to 3× more visitors from AI after installation",
}: TestimonialsCarouselSectionProps) {
  return (
    <section
      id="testimonials"
      className={cn(
        "flex flex-col w-full items-center gap-8 px-6 py-16 lg:py-24",
        className
      )}
    >
      {/* Header */}
      <div className="flex flex-col items-center gap-3 w-full max-w-[520px] lg:max-w-[600px]">
        <h2 className="font-sans font-medium text-[#15151a] text-[30px] lg:text-[40px] text-center tracking-[-0.03em] leading-[1.1]">
          {headerText}
        </h2>
      </div>

      {/* Mobile Layout - Stacked cards */}
      <div className="lg:hidden flex flex-col gap-4 w-full max-w-[400px]">
        {testimonials.map((testimonial, index) => (
          <TestimonialCard key={index} testimonial={testimonial} />
        ))}
      </div>

      {/* Desktop Layout - 3 columns with center feature */}
      <div className="hidden lg:grid grid-cols-[1fr_auto_1fr] gap-8 w-full max-w-[1200px] items-start">
        {/* Left Column */}
        <div className="flex flex-col gap-6">
          {leftTestimonials.map((testimonial, index) => (
            <TestimonialCard key={index} testimonial={testimonial} />
          ))}
        </div>

        {/* Center Feature */}
        <div className="flex justify-center">
          <CenterFeatureCard testimonial={centerTestimonial} />
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-6">
          {rightTestimonials.map((testimonial, index) => (
            <TestimonialCard key={index} testimonial={testimonial} />
          ))}
        </div>
      </div>
    </section>
  );
}
